import React, { useState, useEffect } from 'react';
import { useEnhancedLoading } from '../hooks/useEnhancedLoading.jsx';
import { Icon } from './ui/index.jsx';

const EnhancedAnalysisLoader = ({
    currentStep,
    loadingMessage,
    platform,
    originalIdea,
    isLoading,
    stepTimings = {}, // Agent response timing data
    // 🚀 New workflow props
    workflowMode = false,
    workflowProgress = null,
    workflowStep = null,
    batchProgress = null,
    browserSafe = false,
    startedAt = null, // 🔧 Workflow start timestamp for timer persistence
    onCancel = null   // 🚫 Cancel callback for running workflows
}) => {
    // 🔧 Calculate initial elapsed time from startedAt (persists across navigation)
    const getInitialElapsed = () => {
        if (startedAt && typeof startedAt === 'number') {
            return Math.floor((Date.now() - startedAt) / 1000);
        }
        return 0;
    };

    const [timeElapsed, setTimeElapsed] = useState(0); // per-step timer (seconds)
    const [totalElapsed, setTotalElapsed] = useState(getInitialElapsed); // global timer (seconds)
    const [currentTip, setCurrentTip] = useState(0);
    const [stepTimes, setStepTimes] = useState({}); // Final completion times for completed steps
    const [currentStepStartTime, setCurrentStepStartTime] = useState(Date.now());
    const { updateStep } = useEnhancedLoading();
    const [flowStarted, setFlowStarted] = useState(startedAt ? true : false);
    const globalStartRef = React.useRef(startedAt || null);
    const prevIsLoadingRef = React.useRef(isLoading);

    // Step definitions with detailed information
    const analysisSteps = [
        {
            id: 1,
            title: "Preparing Analysis",
            description: "Setting up your project analysis",
            icon: "settings",
            estimatedTime: "10s",
            color: "blue"
        },
        {
            id: 2,
            title: "Research & Discovery",
            description: `Claude AI is analyzing your idea for ${platform}, researching best practices, and exploring architecture patterns`,
            icon: "search",
            estimatedTime: "60-90s",
            color: "purple",
            details: [
                "Analyzing your project requirements",
                "Researching platform-specific patterns",
                "Identifying key architectural components",
                "Exploring similar applications",
                "Gathering best practices"
            ]
        },
        {
            id: 3,
            title: "Validation & Optimization",
            description: `Claude is validating the research findings and optimizing the architecture for ${platform}`,
            icon: "check",
            estimatedTime: "45-60s",
            color: "green",
            details: [
                "Cross-validating architecture decisions",
                "Optimizing for platform constraints",
                "Identifying potential issues",
                "Refining component relationships",
                "Ensuring scalability patterns"
            ]
        },
        {
            id: 4,
            title: "Final Analysis",
            description: `Claude is creating comprehensive technical recommendations and detailed architecture`,
            icon: "target",
            estimatedTime: "75-120s",
            color: "orange",
            details: [
                "Synthesizing research and validation",
                "Creating detailed architecture",
                "Generating technical specifications",
                "Defining component interactions",
                "Preparing implementation roadmap"
            ]
        },
        {
            id: 5,
            title: "Generating Micro-Prompts",
            description: `Creating precise, actionable development prompts optimized for ${platform} - this is where the magic happens!`,
            icon: "rocket",
            estimatedTime: "60-90s",
            color: "indigo",
            details: [
                "Converting architecture into actionable steps",
                "Creating platform-specific micro-prompts",
                "Adding implementation details & validation",
                "Preparing copy-paste ready prompts",
                "Quality checking for immediate use",
                "This step saves you hours of prompt engineering!"
            ]
        },
        {
            id: 6,
            title: "Generating Flow Diagrams",
            description: `Creating interactive visual diagrams to map out your application's user journeys and system flows`,
            icon: "clipboard",
            estimatedTime: "15-30s",
            color: "teal",
            details: [
                "Generating user journey diagrams",
                "Creating system flow visualizations",
                "Mapping authentication processes",
                "Designing error handling flows",
                "Optimizing for ${platform} patterns",
                "Making diagrams ready for immediate viewing"
            ]
        }
    ];

    // Cloudflare-specific tips
    const cloudflareTips = [
        {
            title: "Cloudflare Free Tier is Generous",
            content: "Workers: 100K requests/day, D1: 5GB + 5M reads/day, KV: 1GB + 100K reads/day, R2: 10GB with zero egress fees, Pages: 500 builds/month. Most MVPs run completely free!"
        },
        {
            title: "Enable R2 Storage Before Deploy",
            content: "If your app needs file uploads, enable R2 in your Cloudflare dashboard first. It takes 30 seconds and gives you 10GB of S3-compatible storage with zero egress costs."
        },
        {
            title: "Cloudflare Access for Private Apps",
            content: "Building an internal tool? Cloudflare Access provides Zero Trust authentication for up to 50 users free. Perfect for company dashboards, admin panels, and team tools."
        },
        {
            title: "D1 is SQLite at the Edge",
            content: "D1 runs SQLite globally at the edge. Use INTEGER for timestamps, datetime('now') for current time. Time Travel gives you 30-day point-in-time recovery free!"
        },
        {
            title: "Workers AI: On-Device AI",
            content: "Need AI features? Workers AI runs models directly at the edge - no external API calls needed. Great for text generation, embeddings, and image classification."
        },
        {
            title: "Wrangler is Your Best Friend",
            content: "The Wrangler CLI handles everything: local dev, deployments, D1 migrations, KV management, and secrets. One tool to rule them all - 'npx wrangler dev' to start!"
        }
    ];

    // Generic "Vibe Coding" tips for other platforms
    const genericTips = [
        {
            title: "Vibe Coding: Let AI Do the Heavy Lifting",
            content: "Describe what you want, and AI coding assistants like Cursor and Claude will generate the code. Focus on the 'what', not the 'how' - that's the vibe coding way!"
        },
        {
            title: "From Vague Ideas to Precise Micro-Prompts",
            content: "The Architect transforms your rough concept into actionable development steps. No more staring at a blank screen wondering 'where do I start?' - just paste and build!"
        },
        {
            title: "Claude-Powered Architecture",
            content: "Claude AI analyzes your idea, researches best practices, validates the architecture, and generates production-ready micro-prompts tailored to your platform."
        },
        {
            title: "Micro-Prompts = Instant Development",
            content: "Each prompt is designed to be copy-pasted directly into your AI coding assistant. No more prompt engineering - just paste and watch your app come to life!"
        },
        {
            title: "Skip the Learning Curve",
            content: "Instead of spending weeks learning platform-specific patterns, get expert-level guidance in minutes. Focus on building, not researching."
        },
        {
            title: "Quality Over Speed",
            content: "This thorough analysis prevents architectural mistakes that could cost weeks to fix later. A few minutes of analysis saves hours of refactoring and debugging."
        }
    ];

    // Select tips based on platform
    const isCloudflare = platform?.toLowerCase() === 'cloudflare';
    const educationalTips = isCloudflare ? cloudflareTips : genericTips;

    // Platform display names
    const platformDisplayNames = {
        'cloudflare': 'Cloudflare (Full Stack)',
        'cursor': 'AI Vibe Coding',
        'replit': 'AI Vibe Coding',
        'loveable': 'AI Vibe Coding',
        'base44': 'AI Vibe Coding',
    };
    const displayPlatform = platformDisplayNames[platform?.toLowerCase()] || 'AI Vibe Coding';

    // Initialize global start from sessionStorage to persist across brief unmounts between steps
    useEffect(() => {
        const savedStart = sessionStorage.getItem('analysisGlobalStartMs');
        if (savedStart && !globalStartRef.current) {
            globalStartRef.current = Number(savedStart);
            setFlowStarted(true);
            setTotalElapsed(Math.floor((Date.now() - globalStartRef.current) / 1000));
        }
    }, []);

    // Detect a brand-new run (transition from idle to loading at early steps) and reset timer explicitly
    useEffect(() => {
        const wasLoading = prevIsLoadingRef.current;
        if (!wasLoading && isLoading && (currentStep === 1 || currentStep === 2)) {
            const now = Date.now();
            globalStartRef.current = now;
            sessionStorage.setItem('analysisGlobalStartMs', String(now));
            setFlowStarted(true);
            setTotalElapsed(0);
        }
        prevIsLoadingRef.current = isLoading;
    }, [isLoading, currentStep]);

    // Global real-time timer - runs from the moment the flow starts until completion
    // 🔧 Uses startedAt prop from workflow context for persistence across navigation
    useEffect(() => {
        // Use startedAt from props if available (workflow context provides this)
        if (startedAt && !globalStartRef.current) {
            globalStartRef.current = startedAt;
            setFlowStarted(true);
            // Calculate elapsed time from startedAt
            const elapsed = Math.floor((Date.now() - startedAt) / 1000);
            setTotalElapsed(elapsed);
        }
        // Initialize if starting fresh and not already set (fallback for non-workflow mode)
        else if (isLoading && !flowStarted && !globalStartRef.current) {
            const now = Date.now();
            globalStartRef.current = now;
            setFlowStarted(true);
            setTotalElapsed(0);
        }

        if (!isLoading) {
            return; // keep final totalElapsed value
        }

        const globalTimer = setInterval(() => {
            if (globalStartRef.current) {
                const secs = Math.floor((Date.now() - globalStartRef.current) / 1000);
                setTotalElapsed(secs);
            }
        }, 1000);

        return () => clearInterval(globalTimer);
    }, [isLoading, flowStarted, startedAt]);

    // Per-step real-time timer - runs for current step only
    useEffect(() => {
        if (!isLoading) return;

        // Reset timer when step changes and record previous step completion
        if (currentStep > 1) {
            const previousStep = currentStep - 1;
            // Record the final time for the previous step before resetting
            if (!stepTimes[previousStep] && timeElapsed > 0) {
                const finalTime = timeElapsed;
                setStepTimes(prev => ({
                    ...prev,
                    [previousStep]: finalTime
                }));
            }
        }

        // Reset timer for new step
        setTimeElapsed(0);
        setCurrentStepStartTime(Date.now());

        // Start real-time counter for current step
        const timer = setInterval(() => {
            setTimeElapsed(prev => prev + 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [currentStep, isLoading]);

    // Record final step completion when analysis finishes
    useEffect(() => {
        if (!isLoading && currentStep > 1 && timeElapsed > 0) {
            const finalStep = currentStep;
            if (!stepTimes[finalStep]) {
                const finalTime = timeElapsed;
                setStepTimes(prev => ({
                    ...prev,
                    [finalStep]: finalTime
                }));
            }
        }
    }, [isLoading]);

    // When a step finishes (progressing to next), freeze its time from agent if available
    useEffect(() => {
        const stepMap = { 2: 'research', 3: 'validation', 4: 'analysis', 5: 'prompts', 6: 'diagram' };
        const prev = currentStep - 1;
        if (isLoading && prev >= 2 && !stepTimes[prev]) {
            const agentKey = stepMap[prev];
            // Handle both 'diagram' (singular) and 'diagrams' (plural) from backend
            const agentMs = stepTimings?.[agentKey]?.processingTime ||
                           (agentKey === 'diagram' ? stepTimings?.['diagrams']?.processingTime : null);
            if (agentMs) {
                const secs = Math.ceil(agentMs / 1000);
                setStepTimes(prevTimes => ({ ...prevTimes, [prev]: secs }));
            }
        }
    }, [currentStep, isLoading, stepTimings, stepTimes]);

    // Tip rotation effect
    useEffect(() => {
        if (!isLoading) return;

        const tipTimer = setInterval(() => {
            setCurrentTip(prev => (prev + 1) % educationalTips.length);
        }, 15000); // Change tip every 15 seconds

        return () => clearInterval(tipTimer);
    }, [isLoading, educationalTips.length]);

    // Update step in enhanced loading hook
    useEffect(() => {
        updateStep(currentStep);
    }, [currentStep, updateStep]);

    // Helper functions
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getStepCompletionTime = (stepId) => {
        // Use real-time client-side timing first (user's preference)
        if (stepTimes[stepId]) {
            const completionTime = formatTime(stepTimes[stepId]);
            return completionTime;
        }

        // Fallback to Agent timing data if available
        const stepMap = {
            2: 'research',
            3: 'validation',
            4: 'analysis',
            5: 'prompts',
            6: 'diagram'  // Backend sends singular
        };

        const agentStepName = stepMap[stepId];
        // Handle both 'diagram' (singular) and 'diagrams' (plural) from backend
        const stepTiming = stepTimings[agentStepName] ||
                          (agentStepName === 'diagram' ? stepTimings['diagrams'] : null);
        if (agentStepName && stepTiming) {
            const processingTimeMs = stepTiming.processingTime;
            const completionTimeSeconds = Math.ceil(processingTimeMs / 1000);
            const completionTime = formatTime(completionTimeSeconds);
            return completionTime;
        }

        return null;
    };

    const getStepProgress = (stepId) => {
        if (currentStep > stepId) return 100;
        if (currentStep === stepId) return 60; // Active step shows as in progress
        return 0;
    };

    const getCurrentStepDetails = () => {
        return analysisSteps.find(step => step.id === currentStep) || analysisSteps[0];
    };

    if (!isLoading) return null;

    const currentStepData = getCurrentStepDetails();

    return (
        <div className="min-h-[calc(100vh-80px)] w-full flex items-center justify-center px-4 py-12 relative z-0 animate-fade-in">
            {/* Background Effects */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#7B61FF]/10 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#BA87FF]/5 rounded-full blur-[100px] animate-pulse-slow lg:delay-1000"></div>
            </div>

            <div className="w-full max-w-5xl relative z-10">
                {/* Main Glass Panel */}
                <div className="glass-panel p-8 md:p-12 rounded-3xl border border-white/5 relative overflow-hidden">
                    {/* Top Glow bar */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#7B61FF] to-transparent opacity-50"></div>

                    {/* Header */}
                    <div className="text-center mb-10">
                        <div className="mb-6 relative inline-block">
                            <div className="absolute inset-0 bg-[#7B61FF] rounded-full blur-2xl opacity-20 animate-pulse"></div>
                            <Icon name={currentStepData.icon} size="3xl" className="relative z-10 text-[#7B61FF] drop-shadow-[0_0_15px_rgba(123,97,255,0.5)]" />
                        </div>

                        <h1 className="text-3xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60 tracking-tight">
                            {workflowMode ? 'AI Workflow Running' : 'Analyzing Your App Idea'}
                        </h1>

                        <div className="text-lg md:text-xl text-[#BA87FF] font-medium mb-6 animate-pulse">
                            {workflowProgress?.message || currentStepData.title}
                        </div>

                        {/* 🚀 Browser Safe Indicator */}
                        {workflowMode && browserSafe && (
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 mb-6">
                                <span className="text-lg">✅</span>
                                <span className="text-sm font-medium">Safe to close browser - workflow continues automatically!</span>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <div className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300 backdrop-blur-sm">
                                {workflowMode ? 'Workflow Time' : 'Total Time'}: <span className="text-white font-mono ml-1">{formatTime(totalElapsed)}</span>
                            </div>
                            {workflowMode && (
                                <div className="px-4 py-1.5 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/30 text-sm text-[#BA87FF] backdrop-blur-sm">
                                    Background Processing
                                </div>
                            )}
                            {workflowMode && onCancel && (
                                <button
                                    onClick={() => onCancel('User cancelled')}
                                    className="px-4 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-sm text-red-400 backdrop-blur-sm hover:bg-red-500/20 hover:border-red-500/40 transition-all cursor-pointer"
                                >
                                    Cancel Workflow
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Progress Steps Grid */}
                    <div className="mb-10">
                        <div className="flex justify-between items-end mb-4 px-1">
                            <span className="text-sm font-medium text-gray-400 uppercase tracking-wider">Analysis Progress</span>
                            <span className="text-sm text-gray-500">Step {Math.min(Math.max(currentStep - 1, 1), analysisSteps.length - 1)} of {analysisSteps.length - 1}</span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {analysisSteps.slice(1).map((step, index) => { // Skip the prep step
                                const isActive = currentStep === step.id;
                                const isCompleted = currentStep > step.id;
                                const progress = getStepProgress(step.id);

                                return (
                                    <div
                                        key={step.id}
                                        className={`
                                            relative p-4 rounded-xl border transition-all duration-500 h-full flex flex-col overflow-hidden group
                                            ${isActive
                                                ? 'bg-[#7B61FF]/10 border-[#7B61FF] shadow-[0_0_20px_rgba(123,97,255,0.15)]'
                                                : isCompleted
                                                    ? 'bg-green-500/5 border-green-500/30'
                                                    : 'bg-white/5 border-white/5 hover:border-white/10'
                                            }
                                        `}
                                    >
                                        {/* Active shimmer effect */}
                                        {isActive && (
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent skew-x-12 translate-x-[-200%] animate-shimmer"></div>
                                        )}

                                        <div className="flex items-start justify-between mb-3 relative z-10">
                                            <Icon
                                                name={step.icon}
                                                size="sm"
                                                className={`
                                                    ${isActive ? 'text-[#7B61FF]' : isCompleted ? 'text-green-400' : 'text-gray-600'}
                                                `}
                                            />
                                            {isCompleted && <div className="text-green-400"><Icon name="check" size="xs" /></div>}
                                            {isActive && <div className="w-2 h-2 rounded-full bg-[#7B61FF] animate-pulse"></div>}
                                        </div>

                                        <h3 className={`text-sm font-semibold mb-1 relative z-10 ${isActive ? 'text-white' : 'text-gray-400'}`}>
                                            {step.title}
                                        </h3>

                                        {/* Progress bar for this step */}
                                        <div className="w-full bg-black/40 rounded-full h-1.5 mt-auto relative z-10 mb-2 overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all duration-1000 ${isCompleted ? 'bg-green-500' :
                                                    isActive ? 'bg-[#7B61FF]' : 'bg-gray-700'
                                                    }`}
                                                style={{ width: `${progress}%` }}
                                            ></div>
                                        </div>

                                        <div className="text-xs text-gray-500 font-mono relative z-10">
                                            {isActive
                                                ? formatTime(timeElapsed)
                                                : isCompleted
                                                    ? (getStepCompletionTime(step.id) || 'Done')
                                                    : '—'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Split Layout: Details & Preview */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                        {/* Current Task Details */}
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
                            <h3 className="text-lg font-bold text-white mb-2 flex items-center">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#7B61FF] mr-3 animate-pulse"></span>
                                Currently Processing
                            </h3>
                            <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                                {workflowProgress?.message || loadingMessage}
                            </p>

                            {/* 🚀 Workflow Batch Progress */}
                            {workflowMode && batchProgress && (
                                <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-4 mb-4">
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-[#BA87FF] text-xs font-bold uppercase tracking-wider">
                                            Batch Processing
                                        </span>
                                        <span className="text-white text-xs font-mono">
                                            {batchProgress.currentBatch || 0}/{batchProgress.totalBatches || 5}
                                        </span>
                                    </div>
                                    <div className="w-full bg-black/50 rounded-full h-2 mb-3">
                                        <div
                                            className="h-2 rounded-full bg-gradient-to-r from-[#7B61FF] to-[#BA87FF] transition-all duration-500 shadow-[0_0_10px_rgba(123,97,255,0.5)]"
                                            style={{ width: `${batchProgress.progressPercent || 0}%` }}
                                        ></div>
                                    </div>
                                    <div className="flex justify-between text-xs text-gray-500">
                                        <span>Prompts: {batchProgress.completedPrompts || 0} / {batchProgress.targetPrompts || 50}</span>
                                        {batchProgress.estimatedTimeRemaining && (
                                            <span>ETA: {batchProgress.estimatedTimeRemaining}</span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Checklists */}
                            {currentStepData.details && (
                                <div className="space-y-3">
                                    {currentStepData.details.map((detail, index) => (
                                        <div key={index} className="flex items-start group">
                                            <div className="w-5 h-5 rounded-full bg-[#7B61FF]/10 flex items-center justify-center mr-3 mt-0.5 group-hover:bg-[#7B61FF]/20 transition-colors">
                                                <div className="w-1.5 h-1.5 bg-[#7B61FF] rounded-full"></div>
                                            </div>
                                            <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">{detail}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Project Preview & Tips */}
                        <div className="flex flex-col gap-6">
                            {/* Project Info */}
                            <div className="bg-black/40 border border-white/5 rounded-2xl p-6">
                                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Project Context</h3>
                                <div className="space-y-4">
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Platform</div>
                                        <div className="text-white font-medium flex items-center gap-2">
                                            {isCloudflare && <img src="https://www.cloudflare.com/favicon.ico" alt="" className="w-4 h-4" />}
                                            {displayPlatform}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500 mb-1">Vision</div>
                                        <div className="text-gray-300 text-sm line-clamp-3 italic">"{originalIdea?.substring(0, 150)}{originalIdea?.length > 150 && '...'}"</div>
                                    </div>

                                    {currentStep === 5 && (
                                        <div className="mt-4 p-3 rounded-lg bg-[#7B61FF]/10 border border-[#7B61FF]/20 flex gap-3 items-start">
                                            <Icon name="sparkles" size="sm" className="text-[#7B61FF] mt-0.5" />
                                            <div>
                                                <div className="text-[#BA87FF] text-xs font-bold mb-0.5">Magic in progress</div>
                                                <div className="text-gray-400 text-xs">Generating high-fidelity micro-prompts for your stack.</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Did You Know? */}
                            <div className="flex-1 bg-gradient-to-br from-[#7B61FF]/10 to-transparent border border-[#7B61FF]/20 rounded-2xl p-6 relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-[#7B61FF]/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:bg-[#7B61FF]/20 transition-colors duration-700"></div>

                                <div className="flex items-center gap-2 mb-3">
                                    <Icon name="lightbulb" size="sm" className="text-[#BA87FF]" />
                                    <span className="text-[#BA87FF] text-xs font-bold uppercase tracking-wider">Did You Know?</span>
                                </div>

                                <h4 className="text-white font-bold mb-2 text-lg">{educationalTips[currentTip].title}</h4>
                                <p className="text-sm text-gray-300 leading-relaxed mb-4">{educationalTips[currentTip].content}</p>

                                <div className="flex gap-1.5 mt-auto">
                                    {educationalTips.map((_, index) => (
                                        <div
                                            key={index}
                                            className={`h-1 rounded-full transition-all duration-300 ${index === currentTip ? 'w-6 bg-[#7B61FF]' : 'w-1.5 bg-white/10'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Expected Time */}
                    <div className="text-center">
                        <p className="text-xs text-gray-500">
                            Expected total time: 5-7 minutes • Quality takes time, but it saves you hours of development work
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnhancedAnalysisLoader;